import React, { useState, useEffect, useMemo, useRef } from "react";
import { XMarkIcon, SparklesIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AssignmentOwnerSelector } from "components/shared/form/AssignmentOwnerSelector";
import { LeadTagSelector } from "components/shared/form/LeadTagSelector";
import { Listbox } from "components/shared/form/Listbox";
import { getStageAndSubStages } from "services/leadStageService";
import { getInternalUsersForAssigningLeads, getSalesConsultantUsers } from "services/userService";
import { Button } from "components/ui";
import { toast } from "sonner";

const AssignmentSection = ({
    onClose,
    assignmentConfig,
    onApply,
    formAssignments = [],
    formValues = {},
    onStagesLoaded,
    inline = false,
    useSalesConsultants = false,
}) => {
    const [stages, setStages] = useState([]);
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(false);

    // Local state for the inputs (initialized from props if present)
    const [localConfig, setLocalConfig] = useState(() => {
        if (assignmentConfig) {
            return {
                stageId: assignmentConfig.stageId ? String(assignmentConfig.stageConfigId || assignmentConfig.stageId) : "",
                subStageId: assignmentConfig.subStageId ? String(assignmentConfig.subStageId) : "",
                mode: assignmentConfig.mode || "owner",
                owners: assignmentConfig.owners || [],
                tags: assignmentConfig.tags || [],
            };
        }
        return {
            stageId: "",
            subStageId: "",
            mode: "owner",
            owners: [],
            tags: [],
        };
    });

    // Fetch stage and owner options
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            setLoading(true);
            try {
                const [stageData, ownerResult] = await Promise.all([
                    getStageAndSubStages({ pageSize: 200 }),
                    useSalesConsultants ? getSalesConsultantUsers() : getInternalUsersForAssigningLeads(),
                ]);

                if (isMounted) {
                    if (Array.isArray(stageData)) {
                        setStages(stageData);
                    }
                    if (ownerResult?.success && Array.isArray(ownerResult.data)) {
                        const mappedOwners = ownerResult.data.map(user => ({
                            id: user.userId || user.id,
                            userId: user.userId || user.id,
                            fullName: user.fullName || user.FullName || user.name || "",
                            value: user.value || user.userId || user.id,
                            label: user.label || user.fullName || user.FullName || user.name || "",
                            designationName: user.designationName || "",
                            designations: user.designations || [],
                            roleName: user.roleName || "",
                        }));
                        setOwners(mappedOwners);
                    }
                }
            } catch (err) {
                console.error("AssignmentSection: failed to load stages/owners", err);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => {
            isMounted = false;
        };
    }, []);

    // Notify parent of loaded stages
    useEffect(() => {
        if (stages.length > 0 && typeof onStagesLoaded === "function") {
            onStagesLoaded(stages);
        }
    }, [stages, onStagesLoaded]);

    // Helper to find default stage ("New" or the first stage)
    const defaultStageId = useMemo(() => {
        if (!stages || stages.length === 0) return "";
        const found = stages.find((s) => s.stageName === "New") || stages[0];
        return found ? String(found.stageId) : "";
    }, [stages]);

    // Sync local state from props only when parent's assignmentConfig actually changes
    const prevAssignmentConfigRef = useRef(assignmentConfig);

    useEffect(() => {
        const hasChanged = JSON.stringify(prevAssignmentConfigRef.current) !== JSON.stringify(assignmentConfig);
        if (hasChanged) {
            prevAssignmentConfigRef.current = assignmentConfig;
            if (assignmentConfig) {
                const nextStageId = assignmentConfig.stageId ? String(assignmentConfig.stageConfigId || assignmentConfig.stageId) : "";
                const nextSubStageId = assignmentConfig.subStageId ? String(assignmentConfig.subStageId) : "";
                const nextMode = assignmentConfig.mode || "owner";
                setLocalConfig({
                    stageId: nextStageId,
                    subStageId: nextSubStageId,
                    mode: nextMode,
                    owners: assignmentConfig.owners || [],
                    tags: assignmentConfig.tags || [],
                });
            }
        }
    }, [assignmentConfig]);

    // Handle field updates in local state
    const updateLocalConfig = (patch) => {
        setLocalConfig((prev) => ({ ...prev, ...patch }));
    };

    // Propagate changes in localConfig to parent immediately
    useEffect(() => {
        if (typeof onApply === "function") {
            prevAssignmentConfigRef.current = localConfig;
            onApply(localConfig);
        }
    }, [localConfig, onApply]);

    const activeStage = stages.find((s) => String(s.stageId) === String(localConfig.stageId || defaultStageId));
    const subStages = activeStage?.subStages || [];

    if (inline) {
        return (
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 animate-in fade-in duration-300">
                {loading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-400 py-6 justify-center">
                        <svg className="animate-spin w-5 h-5 text-primary-500" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Loading details...
                    </div>
                ) : (
                    <div className="rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 p-4 space-y-4 shadow-sm">
                        {/* Stage + SubStage */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 ml-0.5 uppercase tracking-wider">
                                    Stage
                                </label>
                                <Listbox
                                    data={stages}
                                    value={localConfig.stageId || defaultStageId}
                                    onChange={(obj) => updateLocalConfig({ stageId: obj ? String(obj.stageId) : "", subStageId: "" })}
                                    displayField="stageName"
                                    idField="stageId"
                                    placeholder="Select stage"
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 ml-0.5 uppercase tracking-wider">
                                    Sub Stage
                                </label>
                                <Listbox
                                    data={subStages}
                                    value={localConfig.subStageId}
                                    onChange={(obj) => updateLocalConfig({ subStageId: obj ? String(obj.subStageId) : "" })}
                                    disabled={(!localConfig.stageId && !defaultStageId) || subStages.length === 0}
                                    displayField="subStageName"
                                    idField="subStageId"
                                    placeholder={(!localConfig.stageId && !defaultStageId) ? "Select stage first" : subStages.length === 0 ? "No sub-stages" : "Optional"}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Mode + Owner */}
                        <div className="border-t border-gray-100 dark:border-dark-600 pt-4 space-y-3">
                            <div className="flex items-center justify-between w-full">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                    Assignment Mode
                                </span>
                                <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-dark-600 rounded-full p-0.5 border border-gray-200 dark:border-dark-500">
                                    {["rules", "owner"].map((mode) => (
                                        <button
                                            key={mode}
                                            type="button"
                                            onClick={() =>
                                                updateLocalConfig({
                                                    mode,
                                                    owners: mode === "rules" ? [] : localConfig.owners,
                                                })
                                            }
                                            className={clsx(
                                                "px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150 cursor-pointer",
                                                localConfig.mode === mode
                                                    ? "bg-primary-500 text-white shadow-sm"
                                                    : "text-gray-500 dark:text-dark-300 hover:text-gray-800"
                                            )}
                                        >
                                            {mode === "rules" ? "Apply Rules" : "Assign User"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {localConfig.mode === "owner" && (
                                <div className="w-full animate-fadeIn">
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 ml-0.5">
                                        USERS
                                    </label>
                                    {owners.length > 0 ? (
                                        <AssignmentOwnerSelector
                                            data={owners}
                                            value={localConfig.owners}
                                            onChange={(newOwners) => updateLocalConfig({ owners: newOwners })}
                                            displayField="fullName"
                                            idField="userId"
                                            placeholder="Select users..."
                                            className="w-full text-sm"
                                        />
                                    ) : (
                                        <div className="text-sm text-gray-500 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-gray-50 dark:bg-dark-700 px-3 text-center">
                                            No user found to assign
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Tags Selector */}
                        <div className="pt-4 border-t border-gray-100 dark:border-dark-600">
                            <LeadTagSelector
                                value={localConfig.tags}
                                onChange={(newTags) => updateLocalConfig({ tags: newTags })}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative z-10 flex flex-col max-h-[75vh] border-t border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 shadow-[0_-15px_40px_-5px_rgba(0,0,0,0.1)] dark:shadow-[0_-15px_40px_-5px_rgba(0,0,0,0.4)] animate-in slide-in-from-bottom-6 duration-300 rounded-t-2xl -mt-4">
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-dark-700">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
                        Lead Assignment Settings
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-600 dark:hover:text-gray-300 transition-colors focus:outline-none"
                >
                    <XMarkIcon className="w-4.5 h-4.5" />
                </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-3.5 space-y-3">
                {loading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-400 py-3 justify-center">
                        <svg className="animate-spin w-4 h-4 text-primary-500" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Loading details...
                    </div>
                ) : (
                    <div className="rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 p-3 space-y-3 shadow-sm">
                        {/* Stage + SubStage */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 ml-0.5">
                                    Stage
                                </label>
                                <Listbox
                                    data={stages}
                                    value={localConfig.stageId || defaultStageId}
                                    onChange={(obj) => updateLocalConfig({ stageId: obj ? String(obj.stageId) : "", subStageId: "" })}
                                    displayField="stageName"
                                    idField="stageId"
                                    placeholder="Select stage"
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 ml-0.5">
                                    Sub Stage
                                </label>
                                <Listbox
                                    data={subStages}
                                    value={localConfig.subStageId}
                                    onChange={(obj) => updateLocalConfig({ subStageId: obj ? String(obj.subStageId) : "" })}
                                    disabled={(!localConfig.stageId && !defaultStageId) || subStages.length === 0}
                                    displayField="subStageName"
                                    idField="subStageId"
                                    placeholder={(!localConfig.stageId && !defaultStageId) ? "Select stage first" : subStages.length === 0 ? "No sub-stages" : "Optional"}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* Mode + Owner */}
                        <div className="border-t border-gray-100 dark:border-dark-600 pt-2.5 space-y-2">
                            <div className="flex items-center justify-between w-full">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                    Assignment Mode
                                </span>
                                <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-dark-600 rounded-full p-0.5 border border-gray-200 dark:border-dark-500">
                                    {["rules", "owner"].map((mode) => (
                                        <button
                                            key={mode}
                                            type="button"
                                            onClick={() =>
                                                updateLocalConfig({
                                                    mode,
                                                    owners: mode === "rules" ? [] : localConfig.owners,
                                                })
                                            }
                                            className={clsx(
                                                "px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all duration-150 cursor-pointer",
                                                localConfig.mode === mode
                                                    ? "bg-primary-500 text-white shadow-sm"
                                                    : "text-gray-500 dark:text-dark-300 hover:text-gray-800"
                                            )}
                                        >
                                            {mode === "rules" ? "Apply Rules" : "Assign User"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {localConfig.mode === "owner" && (
                                <div className="w-full animate-fadeIn">
                                    {owners.length > 0 ? (
                                        <AssignmentOwnerSelector
                                            data={owners}
                                            value={localConfig.owners}
                                            onChange={(newOwners) => updateLocalConfig({ owners: newOwners })}
                                            displayField="fullName"
                                            idField="userId"
                                            placeholder="Select users..."
                                            className="w-full text-xs"
                                        />
                                    ) : (
                                        <div className="text-xs text-gray-500 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-gray-50 dark:bg-dark-700 px-3 text-center">
                                            No user found to assign
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Tags Selector */}
                        <div className="pt-2.5 border-t border-gray-100 dark:border-dark-600">
                            <LeadTagSelector
                                value={localConfig.tags}
                                onChange={(newTags) => updateLocalConfig({ tags: newTags })}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssignmentSection;
